/**
 * Notifications
 *
 * To add a new notification type:
 *   1. Add the payload type to `src/types/notifications.ts`.
 *   2. Add one entry to `notificationMessages` below.
 *   3. Return `{ message, boldSegments }`.
 *
 */

import { roleLabel } from '@/services/companySettingsService';
import type {
    MessageResult,
    NotificationItem,
    NotificationMessageLookup,
    NotificationMessageRegistry,
    RawNotification,
} from '@/types/notifications';

function bold(...segments: Array<string | number | null | undefined>): string[] {
    return segments
        .filter((s): s is string | number => s !== undefined && s !== null && `${s}`.trim() !== '')
        .map(String);
}

function isPositiveCount(value: unknown): value is number {
    return typeof value === 'number' && value > 0;
}

function cleanText(value: string | null | undefined): string | null {
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function unique(values: string[]): string[] {
    return [...new Set(values)];
}

function naturalJoin(values: string[]): string {
    if (values.length <= 1) return values.join('');
    if (values.length === 2) return values.join(' and ');
    return `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Notification registry
// ─────────────────────────────────────────────────────────────────────────────
const notificationMessages: NotificationMessageRegistry = {
    negotiation_status_changed: (data) => {
        const debtor = data.debtor_name ?? 'Unknown debtor';
        const newStatus = data.new_status_label ?? 'updated';
        const changedBy = data.changed_by ?? 'Someone';

        if (data.changed_by_role === 'debt_settlement_company') {
            const company = data.debt_settlement_company ?? 'a debt settlement company';
            return {
                message: `${debtor} account settlement negotiations have been ${newStatus} by ${changedBy} from ${company}`,
                boldSegments: bold(debtor, newStatus, changedBy, company),
            };
        }
        return {
            message: `${debtor} account settlement negotiations have been ${newStatus} by ${changedBy}`,
            boldSegments: bold(debtor, newStatus, changedBy),
        };
    },

    account_validated: (data) => ({
        message: `Account for ${data.debtor_name} has been validated`,
        boldSegments: bold(data.debtor_name),
    }),

    account_rejected: (data) => ({
        message: `Account for ${data.debtor_name} has been rejected`,
        boldSegments: bold(data.debtor_name),
    }),

    offer_received: (data) => {
        const debtor = data.debtor_name ?? 'this account';
        const offerType = data.offer_type ?? 'offer';
        const offerTypeCapitalized = offerType.charAt(0).toUpperCase() + offerType.slice(1);
        const changedBy = data.changed_by ?? 'Someone';

        if (data.changed_by_role === 'debt_settlement_company') {
            const company = data.debt_settlement_company ?? 'a debt settlement company';
            return {
                message: `${offerTypeCapitalized} for ${debtor} account has been made by ${changedBy} from ${company}`,
                boldSegments: bold(offerTypeCapitalized, debtor, changedBy, company),
            };
        }
        return {
            message: `${offerTypeCapitalized} for ${debtor} account has been made by ${changedBy}`,
            boldSegments: bold(offerTypeCapitalized, debtor, changedBy),
        };
    },

    negotiation_completed: (data) => ({
        message: `Negotiation #${data.negotiation_id} has been completed`,
        boldSegments: bold(`#${data.negotiation_id}`),
    }),

    csv_import_created: (data) => {
        if (data.uploaded_by && data.original_filename) {
            return {
                message: `${data.uploaded_by} started a new CSV import: ${data.original_filename}`,
                boldSegments: bold(data.uploaded_by, data.original_filename),
            };
        }
        if (data.original_filename) {
            return {
                message: `A new CSV import was started: ${data.original_filename}`,
                boldSegments: bold(data.original_filename),
            };
        }
        return { message: 'A new CSV import was started', boldSegments: [] };
    },

    csv_import_completed: (data) => {
        if (data.has_errors === false && data.total_rows !== undefined) {
            return {
                message: `CSV import completed: all ${data.total_rows} rows were imported successfully`,
                boldSegments: bold(data.total_rows),
            };
        }
        if (data.accepted_rows !== undefined && data.total_rows !== undefined) {
            return {
                message: `CSV import completed: ${data.accepted_rows} of ${data.total_rows} rows were imported`,
                boldSegments: bold(data.accepted_rows, data.total_rows),
            };
        }
        if (data.original_filename) {
            return {
                message: `CSV import completed: ${data.original_filename}`,
                boldSegments: bold(data.original_filename),
            };
        }
        return { message: 'CSV import completed', boldSegments: [] };
    },

    csv_import_failed: (data) => {
        if (data.original_filename) {
            return {
                message: `CSV import failed for ${data.original_filename}`,
                boldSegments: bold(data.original_filename),
            };
        }
        return { message: 'CSV import failed', boldSegments: [] };
    },

    company_user_invited: (data) => {
        const changedBy = data.changed_by ?? 'Someone';
        const targetUser = data.target_user_name ?? data.target_user_email ?? 'a company user';
        const targetRole = data.target_user_role ?? 'unknown role';

        return {
            message: `${changedBy} invited ${targetUser} as ${targetRole}`,
            boldSegments: bold(changedBy, targetUser, targetRole),
        };
    },

    company_user_updated: (data) => {
        const changedBy = data.changed_by ?? 'Someone';
        const targetUser = data.target_user_name ?? data.target_user_email ?? 'a company user';
        const statusChange = data.status_change;

        if (statusChange?.old && statusChange?.new) {
            return {
                message: `${changedBy} updated ${targetUser}: status changed from ${statusChange.old} to ${statusChange.new}`,
                boldSegments: bold(changedBy, targetUser, statusChange.old, statusChange.new),
            };
        }

        const updatedFields = data.updated_fields ?? [];

        if (updatedFields.length === 1 && updatedFields[0] === 'role' && data.target_user_role) {
            const newRole = roleLabel(data.target_user_role);
            return {
                message: `${changedBy} set ${targetUser}'s role to ${newRole}`,
                boldSegments: bold(changedBy, targetUser, newRole),
            };
        }

        const fieldLabels: Record<string, string> = {
            name: 'name',
            role: 'role',
            phone: 'phone number',
            status: 'status',
        };

        if (updatedFields.length > 0) {
            const labels = updatedFields.map((field) => fieldLabels[field] ?? field);
            const fieldSummary = naturalJoin(labels);
            return {
                message: `${changedBy} updated ${targetUser}'s ${fieldSummary}`,
                boldSegments: bold(changedBy, targetUser, ...labels),
            };
        }

        return {
            message: `${changedBy} updated ${targetUser}`,
            boldSegments: bold(changedBy, targetUser),
        };
    },

    company_user_invitation_resent: (data) => {
        const changedBy = data.changed_by ?? 'Someone';
        const targetUser = data.target_user_name ?? data.target_user_email ?? 'a company user';

        return {
            message: `${changedBy} resent invitation to ${targetUser}`,
            boldSegments: bold(changedBy, targetUser),
        };
    },

    company_user_invitation_accepted: (data) => {
        const targetUser = data.target_user_name ?? data.changed_by ?? 'A company user';

        return {
            message: `${targetUser} accepted their company user invitation`,
            boldSegments: bold(targetUser),
        };
    },

    company_user_invitation_deleted: (data) => {
        const changedBy = data.changed_by ?? 'Someone';
        const targetUser = data.target_user_name ?? data.target_user_email ?? 'a company user';

        return {
            message: `${changedBy} deleted ${targetUser}'s invitation`,
            boldSegments: bold(changedBy, targetUser),
        };
    },

    company_banking_information_changed: (data) => {
        const changedBy = data.changed_by ?? 'Someone';
        const changes = data.changes ?? [];
        const accountNames = unique([
            ...(data.account_names ?? []).map(cleanText).filter((name): name is string => name !== null),
            ...changes.map((change) => cleanText(change.account_name)).filter((name): name is string => name !== null),
            cleanText(data.primary_account_name),
        ].filter((name): name is string => name !== null));
        const summaryTotal = data.summary?.total;
        const total = isPositiveCount(summaryTotal)
            ? summaryTotal
            : Math.max(accountNames.length, changes.length);
        const firstChange = changes[0];
        const firstAccountName = cleanText(firstChange?.account_name)
            ?? cleanText(data.primary_account_name)
            ?? accountNames[0];

        if (total === 1 && firstAccountName) {
            const actionLabel = {
                created: 'added',
                updated: 'updated',
                deleted: 'deleted',
            }[firstChange?.action ?? ''] ?? 'changed';
            const quotedAccountName = `"${firstAccountName}"`;

            return {
                message: `${changedBy} ${actionLabel} banking account ${quotedAccountName}`,
                boldSegments: bold(changedBy, quotedAccountName),
            };
        }

        if (total > 1 && accountNames.length > 0) {
            const visibleNames = accountNames.slice(0, 3).map((name) => `"${name}"`).join(', ');
            const overflow = accountNames.length > 3 ? ` and ${accountNames.length - 3} more` : '';
            const accountSummary = `${visibleNames}${overflow}`;

            return {
                message: `${changedBy} changed ${total} banking accounts: ${accountSummary}`,
                boldSegments: bold(changedBy, total, accountSummary),
            };
        }

        return {
            message: `${changedBy} changed banking information`,
            boldSegments: bold(changedBy),
        };
    },
};

const notificationMessageLookup = notificationMessages as NotificationMessageLookup;

const COMPANY_SETTINGS_HREF_MAP: Record<string, string> = {
    '/company/settings/users': '/settings?tab=users',
    '/company/settings/banking-information': '/settings?tab=banking-information',
};

function normalizeFrontendHref(path: string): string {
    return COMPANY_SETTINGS_HREF_MAP[path] ?? path;
}

function normalizeNotificationHref(href: string): string {
    const trimmed = href.trim();
    const hashIndex = trimmed.indexOf('#');
    if (hashIndex >= 0) {
        const hashPath = trimmed.slice(hashIndex + 1);
        const appPath = hashPath.startsWith('/') ? hashPath : `/${hashPath}`;
        return normalizeFrontendHref(appPath);
    }

    try {
        const url = new URL(trimmed, window.location.origin);
        if (url.origin === window.location.origin) {
            return normalizeFrontendHref(`${url.pathname}${url.search}${url.hash}`);
        }
    } catch {
        return normalizeFrontendHref(trimmed);
    }

    return normalizeFrontendHref(trimmed);
}

const FALLBACK_MESSAGE: MessageResult = { message: 'New notification', boldSegments: [] };

const NEGOTIATION_STATUS_SOURCE_MAP: Record<string, string> = {
    accepted: 'accepted',
    rejected: 'rejected',
    completed: 'completed',
    pending: 'pending-offers',
};

function deriveHref(notification: RawNotification): string | null {
    if (notification.type === 'negotiation_status_changed') {
        const id = notification.data.negotiation_id;
        const label = typeof notification.data.new_status_label === 'string'
            ? notification.data.new_status_label.toLowerCase()
            : '';
        const source = NEGOTIATION_STATUS_SOURCE_MAP[label];
        if (id !== undefined && id !== null && source) {
            return `/negotiations/${id}?source=${source}`;
        }
    }

    return typeof notification.data.href === 'string' && notification.data.href.trim() !== ''
        ? normalizeNotificationHref(notification.data.href)
        : null;
}

// Named util export keeps existing imports stable.
// eslint-disable-next-line import/prefer-default-export
export function normalizeNotification(notification: RawNotification): NotificationItem {
    const builder = notificationMessageLookup[notification.type];
    const { message, boldSegments } = builder ? builder(notification.data) : FALLBACK_MESSAGE;

    return {
        id: String(notification.id),
        message,
        boldSegments,
        createdAt: notification.created_at,
        isRead: notification.read_at !== undefined && notification.read_at !== null,
        href: deriveHref(notification),
    };
}
